/** Task Management Dashboard — modern light productivity workspace theme. */

export const TASK_MANAGEMENT_DASHBOARD_FILES = {
  'src/index.css': `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

html { scroll-behavior: smooth; }
body { font-family: 'Inter', system-ui, sans-serif; }
`,

  'src/App.js': `import React from 'react';
import './index.css';
import Sidebar from './components/Sidebar';
import TopNav from './components/TopNav';
import AnalyticsOverview from './components/AnalyticsOverview';
import KanbanBoard from './components/KanbanBoard';
import TeamCollaboration from './components/TeamCollaboration';
import TimelineWidget from './components/TimelineWidget';
import InsightsSection from './components/InsightsSection';
import DashboardFooter from './components/DashboardFooter';

export default function App() {
  return (
    <div className="flex min-h-screen bg-[#F4F5F7] text-[#111827] antialiased">
      <Sidebar />
      <div className="flex min-h-screen flex-1 flex-col">
        <TopNav />
        <main className="flex-1 overflow-y-auto px-4 py-5 md:px-6 md:py-6">
          <div className="mx-auto max-w-[1400px] space-y-6">
            <AnalyticsOverview />
            <KanbanBoard />
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <TeamCollaboration />
              </div>
              <TimelineWidget />
            </div>
            <InsightsSection />
            <DashboardFooter />
          </div>
        </main>
      </div>
    </div>
  );
}
`,

  'src/data/dashboard.js': `export const NAV_ITEMS = [
  { label: 'Dashboard', icon: '▦', active: true },
  { label: 'Projects', icon: '◫' },
  { label: 'Tasks', icon: '☑' },
  { label: 'Calendar', icon: '◷' },
  { label: 'Team', icon: '◎' },
  { label: 'Reports', icon: '◔' },
  { label: 'Settings', icon: '⚙' },
];

export const STATS = [
  { label: 'Tasks completed', value: '128', change: '+12%', tone: 'indigo' },
  { label: 'In progress', value: '34', change: '8 active', tone: 'violet' },
  { label: 'Team velocity', value: '92%', change: '+4.2%', tone: 'teal' },
  { label: 'On-time delivery', value: '87%', change: 'This sprint', tone: 'slate' },
];

export const KANBAN = [
  {
    id: 'backlog',
    title: 'Backlog',
    count: 5,
    color: 'border-slate-200',
    tasks: [
      { id: 1, title: 'Research competitor onboarding flows', label: 'Research', labelColor: 'bg-slate-100 text-slate-600', priority: 'Low', due: 'Mar 28', assignee: 'AK', avatar: 'bg-indigo-100 text-indigo-700' },
      { id: 2, title: 'Draft Q2 product roadmap', label: 'Planning', labelColor: 'bg-violet-100 text-violet-700', priority: 'Medium', due: 'Apr 2', assignee: 'MR', avatar: 'bg-violet-100 text-violet-700' },
    ],
  },
  {
    id: 'progress',
    title: 'In Progress',
    count: 4,
    color: 'border-indigo-200',
    tasks: [
      { id: 3, title: 'Design dashboard analytics widgets', label: 'Design', labelColor: 'bg-indigo-100 text-indigo-700', priority: 'High', due: 'Mar 22', assignee: 'SL', avatar: 'bg-teal-100 text-teal-700' },
      { id: 4, title: 'Implement Kanban drag-and-drop', label: 'Engineering', labelColor: 'bg-blue-100 text-blue-700', priority: 'High', due: 'Mar 24', assignee: 'JC', avatar: 'bg-amber-100 text-amber-700' },
      { id: 5, title: 'User testing session prep', label: 'UX', labelColor: 'bg-teal-100 text-teal-700', priority: 'Medium', due: 'Mar 25', assignee: 'EP', avatar: 'bg-rose-100 text-rose-700' },
    ],
  },
  {
    id: 'review',
    title: 'Review',
    count: 3,
    color: 'border-violet-200',
    tasks: [
      { id: 6, title: 'API integration documentation', label: 'Docs', labelColor: 'bg-slate-100 text-slate-600', priority: 'Medium', due: 'Mar 21', assignee: 'TW', avatar: 'bg-indigo-100 text-indigo-700' },
      { id: 7, title: 'Mobile responsive QA pass', label: 'QA', labelColor: 'bg-amber-100 text-amber-700', priority: 'High', due: 'Mar 23', assignee: 'NK', avatar: 'bg-violet-100 text-violet-700' },
    ],
  },
  {
    id: 'done',
    title: 'Done',
    count: 6,
    color: 'border-teal-200',
    tasks: [
      { id: 8, title: 'Sprint planning workshop', label: 'Team', labelColor: 'bg-teal-100 text-teal-700', priority: 'Done', due: 'Mar 18', assignee: 'AK', avatar: 'bg-indigo-100 text-indigo-700' },
      { id: 9, title: 'Notification system rollout', label: 'Feature', labelColor: 'bg-indigo-100 text-indigo-700', priority: 'Done', due: 'Mar 19', assignee: 'MR', avatar: 'bg-violet-100 text-violet-700' },
    ],
  },
];

export const ACTIVITIES = [
  { user: 'Sarah Lin', action: 'moved', target: 'Design dashboard widgets', time: '12m ago', avatar: 'SL', color: 'bg-teal-100 text-teal-700' },
  { user: 'James Chen', action: 'commented on', target: 'Kanban drag-and-drop', time: '34m ago', avatar: 'JC', color: 'bg-amber-100 text-amber-700' },
  { user: 'Emma Park', action: 'completed', target: 'User testing prep', time: '1h ago', avatar: 'EP', color: 'bg-rose-100 text-rose-700' },
  { user: 'Alex Kim', action: 'assigned', target: 'Q2 roadmap draft', time: '2h ago', avatar: 'AK', color: 'bg-indigo-100 text-indigo-700' },
];

export const TEAM = [
  { name: 'Sarah Lin', role: 'Product Designer', avatar: 'SL', color: 'bg-teal-100 text-teal-700', status: 'online' },
  { name: 'James Chen', role: 'Engineer', avatar: 'JC', color: 'bg-amber-100 text-amber-700', status: 'online' },
  { name: 'Emma Park', role: 'UX Research', avatar: 'EP', color: 'bg-rose-100 text-rose-700', status: 'away' },
  { name: 'Alex Kim', role: 'Product Manager', avatar: 'AK', color: 'bg-indigo-100 text-indigo-700', status: 'online' },
];

export const TIMELINE = [
  { date: 'Mar 20', title: 'Design review', project: 'Dashboard v2', color: 'bg-indigo-500' },
  { date: 'Mar 22', title: 'Sprint demo', project: 'Core team', color: 'bg-violet-500' },
  { date: 'Mar 24', title: 'Launch prep', project: 'Mobile app', color: 'bg-teal-500' },
  { date: 'Mar 26', title: 'Stakeholder sync', project: 'Q2 planning', color: 'bg-slate-400' },
];

export const CHART_BARS = [
  { day: 'Mon', value: 62 },
  { day: 'Tue', value: 78 },
  { day: 'Wed', value: 55 },
  { day: 'Thu', value: 88 },
  { day: 'Fri', value: 72 },
  { day: 'Sat', value: 40 },
  { day: 'Sun', value: 35 },
];
`,

  'src/components/Sidebar.js': `import React from 'react';
import { NAV_ITEMS } from '../data/dashboard';

export default function Sidebar() {
  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-[#E5E7EB] bg-white md:flex">
      <div className="flex items-center gap-2.5 border-b border-[#F3F4F6] px-5 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-bold text-white shadow-sm">
          F
        </div>
        <div>
          <p className="text-sm font-semibold text-[#111827]">Flowdesk</p>
          <p className="text-[10px] font-medium text-[#9CA3AF]">Productivity workspace</p>
        </div>
      </div>

      <nav className="flex-1 space-y-0.5 px-3 py-4">
        {NAV_ITEMS.map((item) => (
          <a
            key={item.label}
            href={'#' + item.label.toLowerCase()}
            className={
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ' +
              (item.active
                ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                : 'text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#111827]')
            }
          >
            <span className="text-base opacity-70">{item.icon}</span>
            {item.label}
          </a>
        ))}
      </nav>

      <div className="border-t border-[#F3F4F6] p-4">
        <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50 p-4">
          <p className="text-xs font-semibold text-indigo-900">Upgrade to Pro</p>
          <p className="mt-1 text-[11px] leading-relaxed text-indigo-700/70">Unlock advanced analytics & team insights.</p>
          <button type="button" className="mt-3 w-full rounded-lg bg-indigo-600 py-1.5 text-xs font-semibold text-white transition hover:bg-indigo-700">
            View plans
          </button>
        </div>
      </div>
    </aside>
  );
}
`,

  'src/components/TopNav.js': `import React from 'react';

export default function TopNav() {
  return (
    <header className="sticky top-0 z-20 border-b border-[#E5E7EB] bg-white/80 backdrop-blur-md">
      <div className="flex items-center gap-4 px-4 py-3 md:px-6">
        <div className="flex items-center gap-2 md:hidden">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-xs font-bold text-white">F</div>
        </div>

        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#9CA3AF]">⌕</span>
          <input
            type="text"
            placeholder="Search tasks, projects, people…"
            className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] py-2 pl-9 pr-4 text-sm text-[#374151] outline-none transition focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button type="button" className="hidden rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#374151] transition hover:bg-[#F9FAFB] sm:block">
            + New task
          </button>
          <button type="button" className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition hover:bg-[#F9FAFB]">
            🔔
            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[9px] font-bold text-white">3</span>
          </button>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 text-xs font-bold text-indigo-700 ring-2 ring-white">
            AK
          </div>
        </div>
      </div>
    </header>
  );
}
`,

  'src/components/AnalyticsOverview.js': `import React from 'react';
import { STATS } from '../data/dashboard';

const TONE = {
  indigo: 'from-indigo-500/10 to-indigo-500/5 border-indigo-100 text-indigo-600',
  violet: 'from-violet-500/10 to-violet-500/5 border-violet-100 text-violet-600',
  teal: 'from-teal-500/10 to-teal-500/5 border-teal-100 text-teal-600',
  slate: 'from-slate-500/10 to-slate-500/5 border-slate-200 text-slate-600',
};

export default function AnalyticsOverview() {
  return (
    <section>
      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#111827] md:text-2xl">Good morning, Alex 👋</h1>
          <p className="text-sm text-[#6B7280]">Here&apos;s your team productivity overview for today.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-medium text-[#374151]">This week</button>
          <button type="button" className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm">Export report</button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {STATS.map((stat) => (
          <div
            key={stat.label}
            className={'rounded-xl border bg-gradient-to-br p-4 shadow-sm ' + TONE[stat.tone]}
          >
            <p className="text-xs font-medium text-[#6B7280]">{stat.label}</p>
            <p className="mt-2 text-2xl font-bold text-[#111827]">{stat.value}</p>
            <p className={'mt-1 text-xs font-semibold ' + (stat.tone === 'teal' || stat.tone === 'indigo' ? 'text-emerald-600' : 'text-[#9CA3AF]')}>
              {stat.change}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
`,

  'src/components/KanbanBoard.js': `import React from 'react';
import { KANBAN } from '../data/dashboard';

const PRIORITY = {
  High: 'text-rose-600 bg-rose-50',
  Medium: 'text-amber-600 bg-amber-50',
  Low: 'text-slate-500 bg-slate-100',
  Done: 'text-teal-600 bg-teal-50',
};

function TaskCard({ task }) {
  return (
    <article className="rounded-lg border border-[#E5E7EB] bg-white p-3 shadow-sm transition hover:border-indigo-200 hover:shadow-md">
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className={'rounded-md px-2 py-0.5 text-[10px] font-semibold ' + task.labelColor}>{task.label}</span>
        <span className={'rounded-md px-1.5 py-0.5 text-[10px] font-bold ' + (PRIORITY[task.priority] || PRIORITY.Low)}>{task.priority}</span>
      </div>
      <h3 className="text-sm font-medium leading-snug text-[#111827]">{task.title}</h3>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px] text-[#9CA3AF]">Due {task.due}</span>
        <div className={'flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold ' + task.avatar}>
          {task.assignee}
        </div>
      </div>
    </article>
  );
}

export default function KanbanBoard() {
  return (
    <section className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm md:p-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[#111827]">Sprint board</h2>
          <p className="text-xs text-[#9CA3AF]">Product launch · 18 tasks</p>
        </div>
        <button type="button" className="rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-medium text-[#374151] hover:bg-[#F9FAFB]">
          + Add column
        </button>
      </div>

      <div className="grid gap-4 overflow-x-auto pb-1 md:grid-cols-2 xl:grid-cols-4">
        {KANBAN.map((col) => (
          <div key={col.id} className={'min-w-[220px] rounded-xl border-t-2 bg-[#F9FAFB] p-3 ' + col.color}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[#374151]">{col.title}</h3>
              <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-[#6B7280] shadow-sm">{col.count}</span>
            </div>
            <div className="space-y-2.5">
              {col.tasks.map((task) => (
                <TaskCard key={task.id} task={task} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
`,

  'src/components/TeamCollaboration.js': `import React from 'react';
import { ACTIVITIES, TEAM } from '../data/dashboard';

export default function TeamCollaboration() {
  return (
    <section className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm md:p-5">
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <h2 className="text-base font-semibold text-[#111827]">Team activity</h2>
          <p className="mb-4 text-xs text-[#9CA3AF]">Recent updates across your workspace</p>
          <ul className="space-y-3">
            {ACTIVITIES.map((a, i) => (
              <li key={i} className="flex gap-3 rounded-lg border border-[#F3F4F6] bg-[#FAFAFA] p-3">
                <div className={'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ' + a.color}>
                  {a.avatar}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#374151]">
                    <span className="font-semibold text-[#111827]">{a.user}</span>{' '}
                    {a.action}{' '}
                    <span className="font-medium text-indigo-600">{a.target}</span>
                  </p>
                  <p className="mt-0.5 text-[11px] text-[#9CA3AF]">{a.time}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="text-base font-semibold text-[#111827]">Team members</h2>
          <p className="mb-4 text-xs text-[#9CA3AF]">4 members · 3 online</p>
          <ul className="space-y-2">
            {TEAM.map((member) => (
              <li key={member.name} className="flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-[#F9FAFB]">
                <div className="relative">
                  <div className={'flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ' + member.color}>
                    {member.avatar}
                  </div>
                  <span className={'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ' + (member.status === 'online' ? 'bg-emerald-400' : 'bg-amber-400')} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#111827]">{member.name}</p>
                  <p className="text-xs text-[#9CA3AF]">{member.role}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/TimelineWidget.js': `import React from 'react';
import { TIMELINE } from '../data/dashboard';

export default function TimelineWidget() {
  return (
    <section className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm md:p-5">
      <h2 className="text-base font-semibold text-[#111827]">Upcoming timeline</h2>
      <p className="mb-4 text-xs text-[#9CA3AF]">Project milestones this week</p>
      <ul className="space-y-4">
        {TIMELINE.map((item, i) => (
          <li key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={'h-2.5 w-2.5 rounded-full ' + item.color} />
              {i < TIMELINE.length - 1 && <div className="mt-1 w-px flex-1 bg-[#E5E7EB]" />}
            </div>
            <div className="pb-4">
              <p className="text-[11px] font-semibold text-indigo-600">{item.date}</p>
              <p className="text-sm font-medium text-[#111827]">{item.title}</p>
              <p className="text-xs text-[#9CA3AF]">{item.project}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
`,

  'src/components/InsightsSection.js': `import React from 'react';
import { CHART_BARS } from '../data/dashboard';

export default function InsightsSection() {
  const max = Math.max(...CHART_BARS.map((b) => b.value));

  return (
    <section className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm md:p-5">
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-[#111827]">Productivity insights</h2>
          <p className="text-xs text-[#9CA3AF]">Tasks completed per day · last 7 days</p>
        </div>
        <div className="flex gap-4 text-xs">
          <span className="flex items-center gap-1.5 text-[#6B7280]"><span className="h-2 w-2 rounded-full bg-indigo-500" /> Completed</span>
          <span className="flex items-center gap-1.5 text-[#6B7280]"><span className="h-2 w-2 rounded-full bg-violet-300" /> Target</span>
        </div>
      </div>

      <div className="flex items-end justify-between gap-2 px-2" style={{ height: 140 }}>
        {CHART_BARS.map((bar) => (
          <div key={bar.day} className="flex flex-1 flex-col items-center gap-2">
            <div className="relative flex w-full max-w-[40px] flex-col items-center justify-end" style={{ height: 110 }}>
              <div
                className="w-full rounded-t-md bg-gradient-to-t from-indigo-600 to-indigo-400 transition hover:from-violet-600 hover:to-violet-400"
                style={{ height: (bar.value / max) * 100 + '%', minHeight: 8 }}
              />
              <div className="absolute w-full rounded-t-md border-t-2 border-dashed border-violet-200" style={{ bottom: '72%' }} />
            </div>
            <span className="text-[10px] font-medium text-[#9CA3AF]">{bar.day}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {[
          ['Avg. completion', '74 tasks/day'],
          ['Peak day', 'Thursday'],
          ['Sprint progress', '68% complete'],
        ].map(([label, val]) => (
          <div key={label} className="rounded-lg bg-[#F9FAFB] px-3 py-2.5">
            <p className="text-[10px] font-medium text-[#9CA3AF]">{label}</p>
            <p className="text-sm font-semibold text-[#111827]">{val}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
`,

  'src/components/DashboardFooter.js': `import React from 'react';

export default function DashboardFooter() {
  return (
    <footer className="flex flex-col items-center justify-between gap-2 border-t border-[#F3F4F6] pt-4 sm:flex-row">
      <p className="text-[11px] text-[#9CA3AF]">© 2026 Flowdesk · Productivity workspace</p>
      <div className="flex gap-4 text-[11px] text-[#9CA3AF]">
        {['Help center', 'Keyboard shortcuts', 'Status'].map((link) => (
          <a key={link} href={'#' + link.toLowerCase().replace(/ /g, '-')} className="transition hover:text-indigo-600">
            {link}
          </a>
        ))}
      </div>
    </footer>
  );
}
`,
};
